// src/utils/s3Uploader.js
import { 
  S3Client, 
  PutObjectCommand,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand
} from "@aws-sdk/client-s3";

const s3Client = new S3Client({
  region: process.env.REACT_APP_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_KEY,
  },
  maxAttempts: 3,
  endpoint: `https://s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com`,
  customUserAgent: 'YourAppName/1.0.0',
  forcePathStyle: true,
});

const MAX_RETRIES = 3;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk
const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // Use multipart for files > 100MB
const SECONDARY_BUCKET = 'fink-multiplefilehandler';

const uploadSingleFile = async (bucketName, file, path) => {
  // For small files, use simple PutObject
  if (file.size <= MULTIPART_THRESHOLD) {
    const buffer = await file.arrayBuffer();
    const params = {
      Bucket: bucketName,
      Key: path,
      Body: buffer,
      ContentType: file.type,
      ACL: 'private',
      Metadata: { 'x-amz-meta-custom-header': 'your-custom-value' },
    };
    await s3Client.send(new PutObjectCommand(params));
  } else {
    // For large files (>100MB), use Multipart Upload with 5MB chunks
    const uploadId = await s3Client.send(
      new CreateMultipartUploadCommand({
        Bucket: bucketName,
        Key: path,
        ContentType: file.type,
        ACL: 'private',
        Metadata: { 'x-amz-meta-custom-header': 'your-custom-value' },
      })
    );

    const multipartUploadId = uploadId.UploadId;
    const parts = [];
    const numChunks = Math.ceil(file.size / CHUNK_SIZE);

    try {
      // Upload all parts
      for (let i = 0; i < numChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const buffer = await chunk.arrayBuffer();

        const partResponse = await s3Client.send(
          new UploadPartCommand({
            Bucket: bucketName,
            Key: path,
            UploadId: multipartUploadId,
            PartNumber: i + 1,
            Body: buffer,
          })
        );

        parts.push({
          ETag: partResponse.ETag,
          PartNumber: i + 1,
        });
      }

      // Complete the multipart upload
      await s3Client.send(
        new CompleteMultipartUploadCommand({
          Bucket: bucketName,
          Key: path,
          UploadId: multipartUploadId,
          MultipartUpload: { Parts: parts },
        })
      );
    } catch (error) {
      // Abort on error
      await s3Client.send(
        new AbortMultipartUploadCommand({
          Bucket: bucketName,
          Key: path,
          UploadId: multipartUploadId,
        })
      );
      throw error;
    }
  }

  return `https://${bucketName}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${path}`;
};

export const uploadToS3 = async (file, path, attempt = 0) => {
  try {
    const mainBucket = process.env.REACT_APP_S3_BUCKET_NAME;
    const isZip = file.name.toLowerCase().endsWith('.zip');

    // Upload to main bucket
    const mainUrl = await uploadSingleFile(mainBucket, file, path);

    // If zip, also upload to secondary bucket (async, don't wait)
    if (isZip) {
      const altPath = `${path.replace(/\.[^/.]+$/, '')}.zip`;
      uploadSingleFile(SECONDARY_BUCKET, file, altPath).catch(err =>
        console.warn(`Secondary upload failed for ${file.name}:`, err.message)
      );
    }

    // Return only main bucket URL
    return mainUrl;
  } catch (error) {
    console.error('Error uploading to S3:', error);
    if (attempt < MAX_RETRIES) {
      console.log(`Retrying upload for ${path}, attempt ${attempt + 1}`);
      await new Promise(res => setTimeout(res, 1000 * Math.pow(2, attempt)));
      return uploadToS3(file, path, attempt + 1);
    }
    throw error;
  }
};

export const uploadBatchToS3 = async (
  files,
  basePath,
  progressCallback,
  concurrentUploads = 5
) => {
  const results = [];
  const failures = [];
  let completed = 0;
  const total = files.length;

  for (let i = 0; i < files.length; i += concurrentUploads) {
    const chunk = files.slice(i, i + concurrentUploads);
    const chunkPromises = chunk.map(async (file) => {
      const filePath = file.webkitRelativePath || file.name;
      const s3Path = `${basePath}/${filePath}`;
      try {
        const url = await uploadToS3(file, s3Path);
        results.push({ file, url, success: true });
      } catch (error) {
        failures.push({ file, error: error.message });
        results.push({ file, error: error.message, success: false });
      }
      completed++;
      const percent = Math.round((completed / total) * 100);
      progressCallback?.(percent);
    });

    await Promise.all(chunkPromises);
  }

  return { results, failures };
};
